// Frontend-only validation rules and error messages
export interface ValidationRule {
  validate: (value: unknown) => boolean | string;
  message: string;
}

export interface ValidationRules {
  [fieldKey: string]: ValidationRule[];
}

// Common validation functions
const validations = {
  // Required field validation
  required: (value: unknown): boolean => {
    return value !== null && value !== undefined && value !== '';
  },

  // Number validation
  isNumber: (value: unknown): boolean => {
    return typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)));
  },

  // Positive number validation
  isPositive: (value: unknown): boolean => {
    const num = typeof value === 'number' ? value : Number(value);
    return !isNaN(num) && num > 0;
  },

  // Date validation
  isDate: (value: unknown): boolean => {
    if (!value) return false;
    const date = new Date(value as string);
    return !isNaN(date.getTime());
  },

  // Today or future date validation
  isTodayOrFutureDate: (value: unknown): boolean => {
    if (!value) return false;
    const date = new Date(value as string);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !isNaN(date.getTime()) && date >= today;
  },

  // Percentage validation (0-100)
  isPercentage: (value: unknown): boolean => {
    const num = typeof value === 'number' ? value : Number(value);
    return !isNaN(num) && num >= 0 && num <= 100;
  },

  // Currency validation (positive number)
  isCurrency: (value: unknown): boolean => {
    const num = typeof value === 'number' ? value : Number(value);
    return !isNaN(num) && num >= 0;
  },

  // String length validation
  maxLength: (max: number) => (value: unknown): boolean => {
    return typeof value === 'string' && value.length <= max;
  },

  // Email validation
  isEmail: (value: unknown): boolean => {
    if (!value) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value as string);
  }
};

// Field-specific validation rules
export const fieldValidations: ValidationRules = {
  // Payoff Date validations (payoff-specific min/max business checks are handled in usePayoffValidation)
  payoffDate: [
    {
      validate: validations.required,
      message: 'Payoff date is required'
    },
    {
      validate: validations.isDate,
      message: 'Payoff date must be a valid date'
    }
  ],

  // Loan Amount validations
  loanAmount: [
    {
      validate: validations.required,
      message: 'Loan amount is required'
    },
    {
      validate: validations.isNumber,
      message: 'Loan amount must be a valid number'
    },
    {
      validate: validations.isPositive,
      message: 'Loan amount must be greater than zero'
    }
  ],

  // Interest Rate validations
  interestRate: [
    {
      validate: validations.required,
      message: 'Interest rate is required'
    },
    {
      validate: validations.isNumber,
      message: 'Interest rate must be a valid number'
    },
    {
      validate: validations.isPercentage,
      message: 'Interest rate must be between 0% and 100%'
    }
  ],

  // Maturity Date validations
  maturityDate: [
    {
      validate: validations.required,
      message: 'Maturity date is required'
    },
    {
      validate: validations.isDate,
      message: 'Maturity date must be a valid date'
    },
    {
      validate: validations.isTodayOrFutureDate,
      message: 'Maturity date must be today or in the future'
    }
  ],

  // Payoff Date validations
  transactionDate: [
    {
      validate: validations.required,
      message: 'Payoff date is required'
    },
    {
      validate: validations.isDate,
      message: 'Payoff date must be a valid date'
    },
    {
      validate: validations.isTodayOrFutureDate,
      message: 'Payoff date must be today or in the future'
    }
  ],

  // Principal Balance validations
  principalBalance: [
    {
      validate: validations.isNumber,
      message: 'Principal balance must be a valid number'
    },
    {
      validate: validations.isCurrency,
      message: 'Principal balance must be a positive amount'
    }
  ],

  // Accrued Interest validations
  accruedInterest: [
    {
      validate: validations.isNumber,
      message: 'Accrued interest must be a valid number'
    },
    {
      validate: validations.isCurrency,
      message: 'Accrued interest must be a positive amount'
    }
  ],

  // Total Payoff Amount validations
  totalPayoffAmount: [
    {
      validate: validations.isNumber,
      message: 'Total payoff amount must be a valid number'
    },
    {
      validate: validations.isCurrency,
      message: 'Total payoff amount must be a positive amount'
    }
  ],

  // Number of Installments validations
  numberOfInstallments: [
    {
      validate: validations.isNumber,
      message: 'Number of installments must be a valid number'
    },
    {
      validate: validations.isPositive,
      message: 'Number of installments must be greater than zero'
    }
  ],

  // Account Name validations
  accountName: [
    {
      validate: validations.required,
      message: 'Account name is required'
    },
    {
      validate: validations.maxLength(100),
      message: 'Account name must be 100 characters or less'
    }
  ],

  // Loan Name validations
  name: [
    {
      validate: validations.required,
      message: 'Loan name is required'
    },
    {
      validate: validations.maxLength(100),
      message: 'Loan name must be 100 characters or less'
    }
  ]
};

// Validation utility functions
export const validateField = (fieldKey: string, value: unknown): string | null => {
  const rules = fieldValidations[fieldKey];
  if (!rules) return null;

  for (const rule of rules) {
    const result = rule.validate(value);
    if (result === false) {
      return rule.message;
    }
  }
  return null;
};

export const validateForm = (data: Record<string, unknown>): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  Object.keys(data).forEach(fieldKey => {
    const error = validateField(fieldKey, data[fieldKey]);
    if (error) {
      errors[fieldKey] = error;
    }
  });
  
  return errors;
};

// Custom validation for specific business rules
export const businessValidations = {
  // Validate that payoff amount doesn't exceed loan amount
  payoffAmountExceedsLoan: (payoffAmount: number, loanAmount: number): string | null => {
    if (payoffAmount > loanAmount) {
      return 'Payoff amount cannot exceed the original loan amount';
    }
    return null;
  },

  // Validate that payoff date is not before loan start date
  payoffDateBeforeLoanStart: (payoffDate: string, loanStartDate: string): string | null => {
    const payoff = new Date(payoffDate);
    const start = new Date(loanStartDate);
    if (payoff < start) {
      return 'Payoff date cannot be before the loan start date';
    }
    return null;
  },

  // Validate that payoff date is today or in the future
  payoffDateNotTodayOrFuture: (payoffDate: string): string | null => {
    const payoff = new Date(payoffDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (payoff < today) {
      return 'Payoff date must be today or in the future';
    }
    return null;
  },

  // Validate that interest rate is reasonable
  unreasonableInterestRate: (rate: number): string | null => {
    if (rate > 50) {
      return 'Interest rate seems unusually high. Please verify the value';
    }
    return null;
  },

  fromDateAfterToDate: (fromDate: string, toDate: string): string | null => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      return 'From Date cannot be later than To date';
    }
    return null;
  },

  toDateBeforeFromDate: (toDate: string, fromDate: string): string | null => {
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
      return 'To Date cannot be earlier than From date';
    }
    return null;
  },

  toDateEqualsAsOfDate: (toDate: string, asOfDate: string): string | null => {
    if (toDate && asOfDate && new Date(toDate).getTime() === new Date(asOfDate).getTime()) {
      return 'To Date must be lesser than As of date';
    }
    return null;
  },

  periodExceedsLimit: (fromDate: string, toDate: string, limitInYears = 1): string | null => {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffYears = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24 * 365);
      if (diffYears > limitInYears) {
        return 'You have requested for the statement for a longer duration, system will take time to process and download. Check back after few mins';
      }
    }
    return null;
  },
  // You may call this after backend responds with an "invalid" state
   // 1. Invalid state (called after backend response)
invalidStateCheck : (backendStatus?: string): string | null => {
  if (backendStatus) {
    return 'You cannot make a Payment now as the Loan details are not up to date';
  }
  return null;
},

// 2. Loan status (simple client check, statuses list should match backend)
loanStatusCheck: (loanStatus?: string): string | null => {
  const invalidStatuses = [
        'Partial Application',
        'Pending Approval', 
        'Canceled',
        'Closed - Rescheduled',
        'Disbursed to LO'
      ];
  if (loanStatus && invalidStatuses.includes(loanStatus)) {
    return 'You cannot make a Payment as the current Loan status cannot accept payments';
  }
  return null;
},

// 3. Amount should not exceed outstanding
transactionAmountValidation: (amount: number, outstanding: number): string | null => {
  if (amount > outstanding) {
    return 'Transaction Amount can not be greater than Outstanding amount.';
  }
  return null;
},

// 4. Amount should be > 0
 zeroTxnAmountCheck : (amount: number): string | null => {
  if (amount <= 0) {
    return 'Transaction amount should be greater than zero';
  }
  return null;
},
// 5. Amount required
requiredAmountCheck : (amount: unknown): string | null => {
  if (amount === null || amount === undefined || String(amount).trim() === '') {
    return 'Amount is required';
  }
  return null;
}

}; 
